import React, { memo, useEffect } from 'react'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from 'reactflow'
import { nodeCardClass, nodeChipClass, nodeHandleClass, nodeIcon, nodeTextClass } from '../../nodeCatalog'

const TYPE = 'branchByField'
const Icon = nodeIcon(TYPE)

interface BranchItem {
  id: string
  label: string
}

const BranchByFieldNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const config = data.branch_by_field_config || {}
  const branches: BranchItem[] = config.branches || []
  // Backend defaults has_default to True when the config is absent, so only
  // hide the default outlet when it is explicitly disabled.
  const hasDefault = config.has_default !== false

  // One outlet per branch (+ optional default). Each handle's id MUST equal the
  // branch id so the executor can route by edge.sourceHandle === matched_branch_id.
  const outlets: BranchItem[] = [
    ...branches,
    ...(hasDefault ? [{ id: 'default', label: 'Default' }] : []),
  ]

  // ReactFlow caches each node's handles; without this, a branch added in the
  // config panel renders a handle ReactFlow doesn't know about, so edges drawn
  // from it are silently dropped and the branch ends up unwired at runtime.
  const updateNodeInternals = useUpdateNodeInternals()
  const outletKey = outlets.map((o) => o.id).join(',')
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, outletKey, updateNodeInternals])

  return (
    <div
      className={nodeCardClass(TYPE, selected)}
      style={{ minWidth: Math.max(180, outlets.length * 64) }}
    >
      <Handle type="target" position={Position.Top} className={nodeHandleClass(TYPE)} />
      <div className="flex items-center gap-2">
        <div className={nodeChipClass(TYPE)}>
          <Icon className={`w-4 h-4 ${nodeTextClass(TYPE)}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-emphasis uppercase tracking-wider ${nodeTextClass(TYPE)}`}>Branch</p>
          <p className="text-sm font-medium text-foreground truncate">{data.label || 'Branch by Field'}</p>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground pl-10 capitalize">{branches.length} branches</p>

      {outlets.length > 0 && (
        // Field-value routes (and the Default fallback) are peers with no pass/fail
        // semantics, so every outlet shares the node tone.
        <div className="flex justify-between gap-1 mt-3">
          {outlets.map((outlet) => (
            <div key={outlet.id} className="relative flex-1 flex justify-center">
              <span className={`text-[8px] font-emphasis uppercase tracking-wide truncate max-w-full ${nodeTextClass(TYPE)}`}>
                {outlet.label || outlet.id}
              </span>
              <Handle
                type="source"
                position={Position.Bottom}
                id={outlet.id}
                className={nodeHandleClass(TYPE)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default memo(BranchByFieldNode)
